import { plans } from '@thecompaniesapi/shared/client'
import type { Stripe } from 'stripe'

export const useBillingStore = defineStore('billing', () => {
  const api = useApi()
  const config = useRuntimeConfig()

  const paymentMethods = ref<Stripe.PaymentMethod[]>([])
  const portal = ref<null | Stripe.BillingPortal.Session>(null)
  const subscriptions = ref<Stripe.Subscription[]>([])

  const defaultPaymentMethod = computed(() => {
    return paymentMethods.value.find((paymentMethod) => {
      return paymentMethod.id === subscription.value?.default_payment_method
    })
  })

  const isSubscriptionActive = computed(() => {
    const status = subscription.value?.status

    return status ? ['active', 'trialing'].includes(status) : false
  })

  const subscription = computed(() => {
    return subscriptions.value?.[0] ? subscriptions.value[0] : null
  })

  const subscriptionName = computed((): PlanName => {
    let planName: PlanName = 'free'

    if (subscription.value) {
      const planId = subscription.value.items.data[0]?.price.product

      for (const planKey in plans) {
        const environment = config.public.nuxtEnv === 'production' ? 'production' : 'development'
        const plan = plans[planKey as keyof typeof plans][environment]

        if (plan?.id === planId) {
          planName = planKey as PlanName
        }
      }
    }

    return planName
  })

  const createSubscription = async (planId: string) => {
    try {
      const { data: checkoutSession } = await api.billing.subscriptions.create({ planId })

      return checkoutSession.url
    }
    catch (error) {
      return Promise.reject(error)
    }
  }

  const createOneTimePayment = async (params: {
    amount: number
    credits: number
  }) => {
    try {
      const { data: checkoutSession } = await api.billing.oneTimePayment.create({
        amount: params.amount,
        credits: params.credits,
      })

      return checkoutSession
    }
    catch (error) {
      return Promise.reject(error)
    }
  }

  const fetchPaymentMethods = async () => {
    try {
      const { data: paymentMethodsFetched } = await api.billing.paymentMethods.fetch()

      paymentMethods.value = paymentMethodsFetched
    }
    catch (error) {
      return Promise.reject(error)
    }
  }

  const fetchPortal = async () => {
    try {
      const { data: portalFetched } = await api.billing.portal.fetch()

      portal.value = portalFetched
    }
    catch (error) {
      return Promise.reject(error)
    }
  }

  const fetchSubscriptions = async () => {
    try {
      const { data: subscriptionsFetched } = await api.billing.subscriptions.fetch()

      subscriptions.value = subscriptionsFetched
    }
    catch (error) {
      return Promise.reject(error)
    }
  }

  const renewSubscription = async () => {
    try {
      const { data: subscriptionRenewed } = await api.billing.subscriptions.renew()

      subscriptions.value = [subscriptionRenewed]
    }
    catch (error) {
      return Promise.reject(error)
    }
  }

  // Setters
  const resetStore = () => {
    paymentMethods.value = []
    portal.value = null
    subscriptions.value = []
  }

  return {
    createOneTimePayment,
    createSubscription,
    defaultPaymentMethod,
    fetchPaymentMethods,
    fetchPortal,
    fetchSubscriptions,
    isSubscriptionActive,
    paymentMethods,
    portal,
    renewSubscription,
    resetStore,
    subscription,
    subscriptionName,
    subscriptions,
  }
})
